import type { Catalog } from '@smarton-pbx/shared';

/** The app's frame, sign-in, the account page and notifications. */
export const shell: Catalog = {
  // The app and its frame.
  'This site is not available.': 'Ši svetainė nepasiekiama.',
  // The menu's aria-label (read with "navigation") and the main language's chip.
  Main: 'Pagrindinė',
  // The menu's groups (and the call list's name under Calls).
  'Call history': 'Skambučių istorija',
  Customers: 'Klientai',
  Reports: 'Ataskaitos',
  'menu|Admin': 'Administravimas',
  'Platform console': 'Platformos konsolė',
  'Account suspended': 'Paskyra sustabdyta',
  '{role} · ext. {extension}': '{role} · vid. nr. {extension}',

  // Shared pieces (components/ui, pickers).
  'Table settings': 'Lentelės nustatymai',
  Columns: 'Stulpeliai',
  'Preparing…': 'Ruošiama…',
  'Download Excel': 'Atsisiųsti Excel',
  Yes: 'Taip',
  Link: 'Nuoroda',
  'Works once, until {time}. Send it to the person yourself.': 'Galioja vienam kartui, iki {time}. Nuorodą darbuotojui nusiųskite patys.',
  'Works once, until {time}.': 'Galioja vienam kartui, iki {time}.',
  'Emailed to {email}.': 'Išsiųsta adresu {email}.',
  'Couldn’t email it to {email}. Send it yourself.': 'Nepavyko išsiųsti adresu {email}. Nuorodą nusiųskite patys.',
  Other: 'Kita',
  'Choose a country': 'Pasirinkite šalį',
  'Remove {name}': 'Pašalinti: {name}',
  'Add a language': 'Pridėti kalbą',
  'Choose the main language': 'Pasirinkite pagrindinę kalbą',
  'Add another': 'Pridėkite dar vieną',

  // Sign-in.
  'Sign in to {name}': 'Prisijunkite prie {name}',
  'Welcome back. Use the email your administrator set up for you.': 'Sveiki sugrįžę. Naudokite el. pašto adresą, kurį jums nustatė administratorius.',
  Password: 'Slaptažodis',
  'Sign in': 'Prisijungti',
  'Set up 2-step sign-in': 'Nustatykite dviejų žingsnių prisijungimą',
  'Enter your code': 'Įveskite kodą',
  'Your account needs a second step at sign-in. It takes a minute.': 'Prisijungiant prie jūsų paskyros reikia antro žingsnio. Nustatymas užtruks minutę.',
  'Open your authenticator app and enter the 6-digit code.': 'Atidarykite autentifikavimo programėlę ir įveskite 6 skaitmenų kodą.',
  'Open an authenticator app, such as Google Authenticator, Microsoft Authenticator or 1Password.':
    'Atidarykite autentifikavimo programėlę, pavyzdžiui, Google Authenticator, Microsoft Authenticator ar 1Password.',
  'Scan this code, or type in the key below it.': 'Nuskaitykite šį kodą arba įveskite po juo esantį raktą.',
  'Enter the 6-digit code the app shows.': 'Įveskite programėlėje rodomą 6 skaitmenų kodą.',
  'QR code for your authenticator app': 'QR kodas autentifikavimo programėlei',
  Key: 'Raktas',
  Code: 'Kodas',
  Continue: 'Tęsti',
  // Setting a password from a link.
  'The two passwords are different.': 'Slaptažodžiai nesutampa.',
  'You’re all set': 'Viskas paruošta',
  'Set your password': 'Nustatykite slaptažodį',
  'Your password is saved. Sign in to continue.': 'Slaptažodis išsaugotas. Prisijunkite, kad galėtumėte tęsti.',
  'Choose a password for {name}.': 'Sugalvokite slaptažodį, kuriuo jungsitės prie {name}.',
  'Your password is set.': 'Slaptažodis nustatytas.',
  'This link is incomplete. Open the full link you were given.': 'Ši nuoroda nepilna. Atidarykite visą gautą nuorodą.',
  'At least 12 characters. A few unrelated words work well.': 'Ne mažiau kaip 12 simbolių. Gerai tinka keli nesusiję žodžiai.',
  'Repeat it': 'Pakartokite',
  'Save password': 'Išsaugoti slaptažodį',

  // Your account.
  '{role} in {region} · extension {extension}': '{role}, regionas {region} · vidinis numeris {extension}',
  '{role} · extension {extension}': '{role} · vidinis numeris {extension}',
  'Phone in this browser': 'Telefonas šioje naršyklėje',
  'The language you see this app in, in every browser you sign in to.': 'Kalba, kuria matote šią programą visose naršyklėse, kuriose prisijungiate.',
  '2-step sign-in': 'Dviejų žingsnių prisijungimas',
  On: 'Įjungta',
  Off: 'Išjungta',
  'You enter a code from your authenticator app when you sign in. If you lose your phone, ask an administrator to reset it.':
    'Prisijungdami įvedate kodą iš autentifikavimo programėlės. Jei pamestumėte telefoną, paprašykite administratoriaus nustatyti dviejų žingsnių prisijungimą iš naujo.',
  'Administrators always use a code from an authenticator app when they sign in.': 'Administratoriai prisijungdami visada įveda kodą iš autentifikavimo programėlės.',
  'Current password': 'Dabartinis slaptažodis',
  'New password': 'Naujas slaptažodis',
  'At least 12 characters': 'Ne mažiau kaip 12 simbolių',
  'Password changed. Your other sessions were signed out.': 'Slaptažodis pakeistas. Visur kitur buvote atjungti.',
  'Change password': 'Keisti slaptažodį',

  // Branding (tenant settings and the platform console).
  'Images can be up to 512 KB.': 'Paveikslėlis gali būti ne didesnis nei 512 KB.',
  Replace: 'Pakeisti',
  Upload: 'Įkelti',
  'Product name': 'Produkto pavadinimas',
  'What users see, instead of ours': 'Rodomas naudotojams vietoj mūsų pavadinimo',
  'Email sender name': 'El. laiškų siuntėjo vardas',
  'Shown as the sender of emails': 'Rodomas kaip el. laiškų siuntėjas',
  'Main colour': 'Pagrindinė spalva',
  'Buttons, links, highlights': 'Mygtukai, nuorodos, paryškinimai',
  'Pick the main colour': 'Pasirinkti pagrindinę spalvą',
  'Accent colour': 'Akcento spalva',
  'Used sparingly': 'Naudojama saikingai',
  'Pick the accent colour': 'Pasirinkti akcento spalvą',
  'Saved. Everyone sees the new branding the next time a page loads.': 'Išsaugota. Visi pamatys naują išvaizdą, kai kitą kartą įkels puslapį.',
  'Save branding': 'Išsaugoti išvaizdą',
  Preview: 'Peržiūra',
  Live: 'Tiesiogiai',
  Logo: 'Logotipas',
  'Shown in the sidebar and on the sign-in page': 'Rodomas šoniniame meniu ir prisijungimo puslapyje',
  'Browser tab icon': 'Naršyklės skirtuko piktograma',
  'A small square image': 'Mažas kvadratinis paveikslėlis',

  // Notifications: the bell and the choices.
  Notifications: 'Pranešimai',
  'Notifications, {n} unread': { one: 'Pranešimai, {n} neperskaitytas', few: 'Pranešimai, {n} neperskaityti', other: 'Pranešimai, {n} neperskaitytų' },
  'Mark all read': 'Pažymėti visus perskaitytais',
  'Nothing yet': 'Kol kas nieko nėra',
  'Missed calls and new voicemail appear here.': 'Čia rodomi praleisti skambučiai ir naujos balso žinutės.',
  Unread: 'Neperskaitytas',
  'When the app is closed, with Answer and Decline': 'Kai programa uždaryta, su mygtukais „Atsiliepti“ ir „Atmesti“',
  'Missed call': 'Praleistas skambutis',
  'A call to you that nobody answered': 'Jums skirtas skambutis, į kurį niekas neatsiliepė',
  'New voicemail': 'Nauja balso žinutė',
  'A caller left you (or your queue) a message': 'Skambinantysis paliko žinutę jums (ar jūsų eilei)',
  'Queue caller to call back': 'Eilės skambinantysis, kuriam reikia perskambinti',
  'A call to your queue went unanswered, or the caller asked to be called back': 'Į skambutį jūsų eilėje niekas neatsiliepė arba skambinantysis paprašė perskambinti',
  'Caller waiting long': 'Skambinantysis laukia ilgai',
  'Someone has waited long in a queue you manage': 'Kažkas ilgai laukia jūsų valdomoje eilėje',
  'Weekly coaching digest': 'Savaitės apžvalga su patarimais',
  'Mondays: what went well last week and what to improve': 'Pirmadieniais: kas praėjusią savaitę pavyko ir ką tobulinti',
  'Call blocked': 'Skambutis užblokuotas',
  'Someone tried an emergency, premium-rate or not-allowed number': 'Kažkas bandė skambinti pagalbos, padidinto tarifo ar neleidžiamu numeriu',
  'Carrier not connected': 'Ryšio operatorius neprijungtas',
  'Calls through it may fail': 'Skambučiai per jį gali nepavykti',
  'AI budget used up': 'Išnaudotas DI biudžetas',
  'Call reviews pause until next month': 'DI pokalbių vertinimas sustabdomas iki kito mėnesio',
  'In the app': 'Programoje',
  'On the computer': 'Kompiuteryje',
  'On the phone': 'Telefone',
  'Notifications on this computer are not set up on this server.': 'Pranešimai kompiuteryje šiame serveryje neįjungti.',
  '“On this computer” works while the browser is running, even with the app closed, once you have allowed notifications in this browser. Nothing arrives when the browser is shut, the computer is asleep or do-not-disturb is on.':
    '„Kompiuteryje“ veikia, kol naršyklė įjungta, net kai programa uždaryta, jei šioje naršyklėje leidote rodyti pranešimus. Kai naršyklė išjungta, kompiuteris veikia miego režimu arba įjungtas režimas „Netrukdyti“, niekas neateina.',
  'Saved.': 'Išsaugota.',
};
